import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, Award, Clock, ThumbsUp } from "lucide-react";
import ReviewsSection from "../components/ReviewsSection";

export default function Home() {
  return (
    <div className="bg-white text-neutral-900">
      {/* Hero Section */}
      <section className="relative bg-black text-white min-h-[50vh] md:min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/4b0ed4f1-460c-426e-a80d-63f8321daac9.jpg"
            alt="Hillerød Smørrebrød Hero"
            className="w-full h-full object-cover opacity-75"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 md:py-20 w-full">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-neutral-400 block mb-3">
              Velkommen til Hillerød Smørrebrød
            </span>
            <h1 className="text-3xl md:text-6xl font-serif font-bold tracking-tight leading-tight mb-6">
              Smørrebrød og sandwich med friske råvarer
            </h1>
            <p className="text-base md:text-lg text-neutral-300 mb-8 leading-relaxed font-light">
              Har du lyst til en sandwich eller noget højtbelagt smørrebrød? Hos Hillerød Smørrebrød tilbyder vi et stort udvalg af alt fra lækre sandwich til hurtige håndmadder og luksussmørrebrød.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/menukort"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300"
              >
                <span>Se Menukort</span>
                <ArrowRight size={14} />
              </Link>
              <a
                href="tel:48266613"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-transparent hover:bg-white hover:text-black border border-white text-white transition-all duration-300"
              >
                <Phone size={14} />
                <span>Ring 48 26 66 13</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-neutral-950 text-white py-8 border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="text-white">
              <Award size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-sm uppercase tracking-wider">10 års erfaring</h3>
              <p className="text-xs text-neutral-400 mt-1">Vi garanterer kompromisløs kvalitet og service.</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="text-white">
              <ShieldCheck size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-sm uppercase tracking-wider">Friske råvarer</h3>
              <p className="text-xs text-neutral-400 mt-1">Altid frisklavet mad med råvarer af højeste kvalitet.</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="text-white">
              <Clock size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-sm uppercase tracking-wider">Hurtig bestilling</h3>
              <p className="text-xs text-neutral-400 mt-1">Ring på forhånd og få maden klar til afhentning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2d5a27] block mb-2">
                Vores Butik
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900 tracking-tight mb-6">
                Lækkert smørrebrød leveret direkte til dig
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                Vores butik er beliggende i Hillerød, men vi leverer gerne lækkert smørrebrød til dig. Vi tilbyder smørrebrød af højeste kvalitet. Vores smørrebrød er altid lavet af friske råvarer og vi laver altid mad, som smager af noget.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Har du specifikke ønsker til noget, så kan vi selvfølgelig også være behjælpelige her. Vi kan hjælpe med mad til festmiddage, receptioner og andre arrangementer, samt frokost til dig selv og aftensmad.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300"
                >
                  Bestil nu
                </Link>
                <Link
                  to="/om-os"
                  className="inline-flex items-center justify-center px-2 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-800 hover:text-[#2d5a27] transition-all duration-300"
                >
                  Læs om os
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative">
                <img
                  src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/b64b104c-6f24-4ab1-9688-7fb928c52b4e.jpg"
                  alt="Smørrebrød i Hillerød"
                  className="w-full md:max-w-md md:mx-auto lg:max-w-none h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Column Services Section */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Smørrebrød Card */}
            <div className="bg-neutral-900 border border-neutral-700 overflow-hidden flex flex-col justify-between group hover:border-neutral-500 transition-colors duration-300">
              <div>
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/1c856953-41f0-474a-9a12-12b972ea5000.jpg"
                    alt="Smørrebrød"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-4">Smørrebrød</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-4">
                    Vi tilbyder smørrebrød af højeste kvalitet. Vores smørrebrød er altid lavet af friske råvarer og vi laver altid mad, som smager af noget.
                  </p>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    Ring gerne for at bestille maden på forhånd. Er du interesseret i højtbelagt smørrebrød eller en lækker sandwich, så kom ind forbi vores forretning i dag og lav en bestilling eller hør mere om hvad vi kan tilbyde.
                  </p>
                </div>
              </div>
              <div className="p-8 pt-0">
                <Link
                  to="/menukort"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white hover:text-neutral-300 transition-colors"
                >
                  <span>Se menukortet</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Firmaordning Card */}
            <div className="bg-neutral-900 border border-neutral-700 overflow-hidden flex flex-col justify-between group hover:border-neutral-500 transition-colors duration-300">
              <div>
                <div className="h-64 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/898aa03e-7ed9-493b-80a1-3c235fae6c88.jpg"
                    alt="Firmaordning"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-serif font-bold text-white mb-4">Firmaordning</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-4">
                    Ønsker I lidt ekstra af det sunde i det daglige – så er en frokostordning fra Hillerød Smørrebrød det helt rigtige valg.
                  </p>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    Hos os er kvalitet og service i top og vores frokostkoncepter er præget af personlighed krydret med passion for det, vi laver.
                  </p>
                </div>
              </div>
              <div className="p-8 pt-0">
                <Link
                  to="/firmaordning"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white hover:text-neutral-300 transition-colors"
                >
                  <span>Læs om firmaordning</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection limit={6} />

      {/* Call to Action Section */}
      <section className="py-20 bg-black text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-45">
          <img
            src="https://assets.auxodev.dk/assets/0fa1183b-3f51-46de-aff6-df593e862933/53240b71-1624-437a-8c9d-0f732bdcae67.jpg"
            alt="Smørrebrød baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight mb-6">
            Klar til at bestille?
          </h2>
          <p className="text-neutral-300 text-sm md:text-lg mb-8 max-w-xl mx-auto font-light">
            Du kan ringe til os på telefon <strong className="text-white">48 26 66 13</strong> hvis du har spørgsmål, eller hvis du vil bestille på forhånd. Du kan endvidere skrive til os på <strong className="text-white">kontakt@hilleroedsmoerrebroed.dk</strong>.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="tel:48266613"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-[#2d5a27] hover:bg-[#20401c] text-white transition-all duration-300"
            >
              <Phone size={14} />
              <span>Ring op nu: 48 26 66 13</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-4 text-xs font-semibold uppercase tracking-wider bg-transparent hover:bg-white hover:text-black border border-white text-white transition-all duration-300"
            >
              Skriv en besked
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
